

import { useState, useEffect, useMemo } from "react";
import { 
  Search, 
  Receipt,
  Tag,
  Plus,
  Pencil,
  Trash2,
  FileText,
  ExternalLink,
  Calendar,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import DateRangePicker from "../components/DateRangePicker";
import SearchableDropdown from "../components/SearchableDropdown";
import ExpenseModal from "./ExpenseModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { expensesApi, vendorsApi, eventsApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

// Types
interface Expense {
  id: number;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_category_id?: number;
  category?: { id: number; name: string };
  vendor_id?: number;
  vendor?: { id: number; name: string };
  event_id?: number;
  event?: { id: number; name: string };
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  proof_path?: string;
  status: string;
  created_at?: string;
}

// Search Input Component
function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search expenses..."
        aria-label="Search expenses"
        className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
      />
    </div>
  );
}

export default function ExpensesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const canDelete = user?.role === 'admin' || user?.role === 'master' || user?.role === 'super_admin';
  
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [vendors, setVendors] = useState<{ id: number; name: string }[]>([]);
  const [events, setEvents] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Date filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('all');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState<Expense | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesRes, categoriesRes, vendorsRes, eventsRes] = await Promise.all([
        expensesApi.list(),
        mastersApi.expenseCategories.list(),
        vendorsApi.list(),
        eventsApi.list(),
      ]);
      
      // Handle different response formats
      const expensesData = expensesRes.data?.data || expensesRes.data || [];
      setExpenses(Array.isArray(expensesData) ? expensesData : []);
      
      const categoriesData = categoriesRes.data?.data || categoriesRes.data || [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      
      const vendorsData = vendorsRes.data?.data || vendorsRes.data || [];
      setVendors(Array.isArray(vendorsData) ? vendorsData : []);
      
      const eventsData = eventsRes.data?.data || eventsRes.data || [];
      setEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (error: any) {
      console.error("Error loading expenses:", error);
      showToast(error?.message || "Failed to load expenses data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingExpense(null);
    setIsModalOpen(true);
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleDelete = (expense: Expense) => {
    setDeletingExpense(expense);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingExpense) return;
    
    try {
      setIsDeleting(true);
      await expensesApi.delete(deletingExpense.id);
      showToast("Expense deleted successfully", "success");
      setIsDeleteModalOpen(false);
      setDeletingExpense(null);
      loadData();
    } catch (error: any) {
      console.error("Error deleting expense:", error);
      showToast(error?.message || "Failed to delete expense", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredExpenses = useMemo(() => {
    let filtered = expenses;
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        (e.title || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.category?.name || '').toLowerCase().includes(q) ||
        (e.vendor?.name || '').toLowerCase().includes(q) ||
        (e.event?.name || '').toLowerCase().includes(q) ||
        (e.reference_number || '').toLowerCase().includes(q)
      );
    }
    
    // Date range filter (supports open-ended ranges: only from, only to, or both)
    if (fromDate || toDate) {
      filtered = filtered.filter(e => {
        const expenseDate = new Date(e.expense_date);
        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          if (expenseDate < from) return false;
        }
        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          if (expenseDate > to) return false;
        }
        return true;
      });
    }
    
    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(e => String(e.expense_category_id) === selectedCategory);
    }
    
    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(e => String(e.vendor_id) === vendorFilter);
    }
    
    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(e => String(e.event_id) === eventFilter);
    }
    
    return filtered;
  }, [expenses, searchQuery, fromDate, toDate, selectedCategory, vendorFilter, eventFilter]);

  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  }, [filteredExpenses]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 mb-1">Expenses Report</h1>
          <p className="text-sm text-stone-500">View and analyze expense records</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Expense
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Search</label>
            <SearchInput value={searchQuery} onChange={setSearchQuery} />
          </div>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={setFromDate}
            onToChange={setToDate}
          />
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Category</label>
            <SearchableDropdown
              options={[
                { value: "all", label: "All Categories" },
                ...categories.map((c) => ({
                  value: String(c.id),
                  label: c.name,
                })),
              ]}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Select category"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Vendor</label>
            <SearchableDropdown
              options={[
                { value: "all", label: "All Vendors" },
                ...vendors.map((v) => ({
                  value: String(v.id),
                  label: v.name,
                })),
              ]}
              value={vendorFilter}
              onChange={setVendorFilter}
              placeholder="Select vendor"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-1.5">Event</label>
            <SearchableDropdown
              options={[
                { value: "all", label: "All Events" },
                ...events.map((e) => ({
                  value: String(e.id),
                  label: e.name,
                })),
              ]}
              value={eventFilter}
              onChange={setEventFilter}
              placeholder="Select event"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {filteredExpenses.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Receipt className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No expenses found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-amber-50 to-amber-100 border-b-2 border-amber-200">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Title</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden lg:table-cell">Date</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide hidden xl:table-cell">Vendor/Event</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Proof</th>
                  <th className="text-right px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Amount</th>
                  <th className="text-center px-4 py-3 text-[10px] font-bold text-stone-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredExpenses.map((expense, idx) => (
                  <tr key={expense.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-stone-800 text-sm">{expense.title}</div>
                      {expense.description && (
                        <div className="text-xs text-stone-500 mt-0.5 truncate max-w-[200px]">{expense.description}</div>
                      )}
                      {expense.reference_number && (
                        <div className="text-xs text-stone-400 mt-0.5">Ref: {expense.reference_number}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1.5 text-sm text-stone-600">
                        <Tag className="w-3.5 h-3.5 text-stone-400" />
                        {expense.category?.name || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600 hidden lg:table-cell">
                      {formatDate(expense.expense_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-600 hidden xl:table-cell">
                      {expense.vendor?.name || expense.event?.name || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {expense.proof_path ? (
                        <a
                          href={getImageUrl(expense.proof_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
                          title="View proof"
                        >
                          <FileText className="w-4 h-4" />
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-stone-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-bold text-amber-700 text-sm">{formatCurrencyFull(expense.amount)}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="p-1.5 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Edit"
                          aria-label="Edit expense"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(expense)}
                            className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                            aria-label="Delete expense"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Total Row */}
              <tfoot className="bg-gradient-to-r from-amber-100 to-amber-50 border-t-2 border-amber-300">
                <tr>
                  <td className="px-4 py-3 font-bold text-stone-800 text-sm">
                    Total Expenses ({filteredExpenses.length}):
                  </td>
                  <td className="hidden md:table-cell"></td>
                  <td className="hidden lg:table-cell"></td>
                  <td className="hidden xl:table-cell"></td>
                  <td></td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-bold text-amber-800 text-base">{formatCurrencyFull(totalAmount)}</div>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      <ExpenseModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingExpense(null);
        }}
        expense={editingExpense}
        categories={categories}
        vendors={vendors}
        events={events}
        onExpenseSaved={loadData}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingExpense(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Expense"
        message={`Are you sure you want to delete the expense "${deletingExpense?.title}"? This action cannot be undone.`}
        loading={isDeleting}
      />
    </DashboardLayout>
  );
}
